import { groupBy } from '@/utils/helpers/formatting'
import type { FetchProductionMean } from '@/views/administrative/management/production-mean/types'
import type { FetchWorkCondition } from '@/views/administrative/management/work-condition/types'
import type { OrganizationDetails } from '@/views/itineraries/areas/courses/curricular-organization/types'
import type {
  CnaeDivision,
  CnaeGroup,
  CnaeSection,
  CourseVersion,
  FunctionCourseVersion,
  InfrastructureWithIds,
  ModuleCurricularUnit,
  ModuleVersion,
} from '@/views/itineraries/areas/types'
import type {
  FetchEvolution,
  FetchRelatedFormation,
} from '@/views/itineraries/updates/courses/work-context/types'

export const useCourseVersion = defineStore('courseVersion', () => {
  const workConditionsStore = ref<FetchWorkCondition[]>([])
  const productionMeansStore = ref<FetchProductionMean[]>([])
  const evolutionsStore = ref<FetchEvolution[]>([])
  const relatedFormationsStore = ref<FetchRelatedFormation[]>([])
  const cnaeStore = ref<CnaeSection[]>([])
  const functionsStore = ref<FunctionCourseVersion[]>([])
  const modalFunctionsStore = ref<FunctionCourseVersion[]>([])
  const infrastructuresStore = ref<InfrastructureWithIds[]>([])

  const allCoursesInAreaStore = ref<CourseVersion[]>([])
  const moduleCurricularUnitsStore = ref<ModuleCurricularUnit[]>([])
  const modulesStore = ref<ModuleVersion[]>([])
  const modalModuleCurricularUnitsStore = ref<OrganizationDetails[]>([])
  const isUpdateNameStore = ref<boolean>(false)
  const isUpdateSubfunctionStore = ref<boolean>(false)
  const refetchCoursesCallback = ref<number>(0)
  const isImportingCourses = ref<boolean>(false)

  const reset = () => {
    workConditionsStore.value = []
    productionMeansStore.value = []
    evolutionsStore.value = []
    relatedFormationsStore.value = []
    cnaeStore.value = []
    functionsStore.value = []
    modalFunctionsStore.value = []
    allCoursesInAreaStore.value = []
    moduleCurricularUnitsStore.value = []
    modulesStore.value = []
    modalModuleCurricularUnitsStore.value = []
    infrastructuresStore.value = []
    isUpdateNameStore.value = false
    isUpdateSubfunctionStore.value = false
    refetchCoursesCallback.value = 0
  }

  function addCnaeSection(section: CnaeSection) {
    cnaeStore.value.push(section)
    cnaeStore.value.sort((sectionA, sectionB) => {
      return sectionA.code.localeCompare(sectionB.code)
    })
  }

  function addCnaeDivision(section: CnaeSection, division: CnaeDivision) {
    const sectionInStore = cnaeStore.value.find((_section) => {
      return _section.id === section.id
    })

    if (!sectionInStore) return

    const divisions = sectionInStore.divisions

    if (!divisions) {
      sectionInStore.divisions = [division]

      return
    }

    divisions.push(division)

    divisions.sort((divisionA, divisionB) => {
      return divisionA.code.localeCompare(divisionB.code)
    })
  }

  function addCnaeGroup(
    section: CnaeSection,
    division: CnaeDivision,
    group: CnaeGroup,
  ) {
    const sectionInStore = cnaeStore.value.find((_section) => {
      return _section.id === section.id
    })

    if (!sectionInStore) return

    const divisions = sectionInStore.divisions
    if (!divisions) return

    const divisionInStore = divisions.find((_division) => {
      return _division.id === division.id
    })

    if (!divisionInStore) return

    const groups = divisionInStore.groups
    if (!groups) {
      divisionInStore.groups = [group]

      return
    }

    groups.push(group)
    groups.sort((groupA, groupB) => {
      return groupA.code.localeCompare(groupB.code)
    })
  }

  function removeCnaeSection(sectionId: string) {
    const dataIndex = cnaeStore.value.findIndex(
      (section) => section.id === sectionId,
    )

    if (dataIndex !== -1) cnaeStore.value.splice(dataIndex, 1)
  }

  function removeCnaeDivision(sectionId: string, divisionId: string) {
    const sectionInStore = cnaeStore.value.find((section) => {
      return section.id === sectionId
    })

    if (!sectionInStore) return

    const divisions = sectionInStore.divisions

    if (!divisions) return

    const dataIndex = divisions.findIndex(
      (division) => division.id === divisionId,
    )

    if (dataIndex !== -1) divisions.splice(dataIndex, 1)
  }

  function removeCnaeGroup(
    sectionId: string,
    divisionId: string,
    groupId: string,
  ) {
    const sectionInStore = cnaeStore.value.find((section) => {
      return section.id === sectionId
    })

    if (!sectionInStore) return

    const divisions = sectionInStore.divisions

    if (!divisions) return

    const divisionInStore = divisions.find(
      (division) => division.id === divisionId,
    )

    if (!divisionInStore) return

    const groups = divisionInStore.groups

    if (!groups) return

    const dataIndex = groups.findIndex((group) => group.id === groupId)

    if (dataIndex !== -1) groups.splice(dataIndex, 1)
  }

  function sortInfrastructuresStore() {
    Object.entries(
      groupBy(
        infrastructuresStore.value,
        ({ typeDescription }) => typeDescription,
      ),
    )
      .toSorted(([typeDescriptionA, _], [typeDescriptionB, __]) =>
        typeDescriptionA.localeCompare(typeDescriptionB),
      )
      .flatMap(([_, infrastructureList]) => {
        return infrastructureList!.toSorted((infraA, infraB) =>
          infraA.description.localeCompare(infraB.description),
        )
      })
  }

  return {
    workConditionsStore,
    productionMeansStore,
    evolutionsStore,
    relatedFormationsStore,
    cnaeStore,
    functionsStore,
    modalFunctionsStore,
    allCoursesInAreaStore,
    infrastructuresStore,
    moduleCurricularUnitsStore,
    modulesStore,
    modalModuleCurricularUnitsStore,
    reset,
    addCnaeSection,
    addCnaeDivision,
    addCnaeGroup,
    removeCnaeSection,
    removeCnaeDivision,
    removeCnaeGroup,
    sortInfrastructuresStore,
    isUpdateNameStore,
    isUpdateSubfunctionStore,
    refetchCoursesCallback,
    isImportingCourses,
  }
})
